import { html, nothing } from "lit";
import type { ExecutionProfileDraft, ValidationError, MultiAgentSettingsProfileTab } from "./types";
import { STATIC_MODEL_PROVIDERS } from "../../controllers/multi-agent/settings";
// JAISIU-878: Nested profile view imports
import {
  renderNestedProfile,
  renderNestingValidation,
  renderExpandedGraph,
  type NestedProfileDisplay,
  type ProfileTreeNode,
} from "./nested-profile-view";
import { ARCHITECTURE_TYPES, DEFAULT_EXECUTION_LIMITS } from "./types";
import {
  renderSequentialWorkflowBuilder,
  renderSupervisorWorkflowBuilder,
  renderParallelWorkflowBuilder,
  renderCustomGraphBuilder,
  renderValidationResult,
  validateExecutionGraph,
  type SequentialBuilderProps,
  type SupervisorBuilderProps,
  type ParallelBuilderProps,
  type CustomGraphBuilderProps,
} from "./workflow-builders";

export type ProfileFormProps = {
  draft: ExecutionProfileDraft;
  activeTab: MultiAgentSettingsProfileTab;
  onDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onAgentAdd: (agentId: string) => void;
  onAgentRemove: (agentId: string) => void;
  onAgentReorder: (from: number, to: number) => void;
  errors: ValidationError[];
  // Builder state
  selectedNodeId?: string | null;
  onNodeSelect?: (id: string | null) => void;
  // JAISIU-878: Nested profile props
  nestingTree?: ProfileTreeNode | null;
  nestingValidation?: {
    valid: boolean;
    cycles: string[][];
    maxDepth: number;
    maxAllowedDepth: number;
  } | null;
  expandedGraph?: {
    nodes: Array<{
      id: string;
      name: string;
      role: string;
      type: string;
      metadata?: Record<string, unknown>;
    }>;
    edges: Array<{ from: string; to: string; type: string }>;
  } | null;
  onNestingToggle?: (profileId: string) => void;
  onExpandProfile?: (profileId: string) => void;
  onValidateNesting?: (profileId: string) => void;
};

// ─── Tab Router ───────────────────────────────────────────────────────────────

export function renderExecutionProfileForm(props: ProfileFormProps) {
  switch (props.activeTab) {
    case "basic":
      return renderBasicTab(props);
    case "agents":
      return renderAgentsTab(props);
    case "model":
      return renderModelTab(props);
    case "tools":
      return renderToolsTab(props);
    case "nesting":
      return renderNestingTab(props);
    case "limits":
      return renderLimitsTab(props);
    case "builder":
      return renderBuilderTab(props);
    case "preview":
      return renderPreviewTab(props);
    default:
      return html`
        <div class="muted">Unknown tab.</div>
      `;
  }
}

// ─── Basic Tab ────────────────────────────────────────────────────────────────

function renderBasicTab({ draft, onDraftUpdate, errors }: ProfileFormProps) {
  const nameErr = errors.find((e) => e.field === "name");

  return html`
    <div class="ma-form-section">
      <div class="card-title">Basic Information</div>

      <label class="field" style="margin-top: 16px;">
        <span>Profile Name <span class="required">*</span></span>
        <input
          type="text"
          class="input ${nameErr ? "input--error" : ""}"
          .value=${draft.name}
          @input=${(e: Event) => onDraftUpdate({ name: (e.target as HTMLInputElement).value })}
          placeholder="e.g. Code Review Pipeline"
        />
        ${nameErr ? html`<span class="field-error">${nameErr.message}</span>` : nothing}
      </label>

      <label class="field" style="margin-top: 16px;">
        <span>Description</span>
        <textarea
          class="input"
          rows="3"
          .value=${draft.description}
          @input=${(e: Event) => onDraftUpdate({ description: (e.target as HTMLTextAreaElement).value })}
          placeholder="What does this profile do?"
        ></textarea>
      </label>
    </div>

    <div class="ma-form-section">
      <div class="card-title">Architecture Type</div>
      <div class="card-sub">Choose how agents interact with each other.</div>

      <div class="ma-arch-grid" style="margin-top: 16px;">
        ${ARCHITECTURE_TYPES.map(
          (arch) => html`
            <div
              class="ma-arch-card ${draft.architectureType === arch.value ? "ma-arch-card--selected" : ""}"
              @click=${() => onDraftUpdate({ architectureType: arch.value })}
            >
              <div class="ma-arch-card-icon">${arch.icon}</div>
              <div class="ma-arch-card-label">${arch.label}</div>
              <div class="ma-arch-card-desc muted">${arch.description}</div>
            </div>
          `,
        )}
      </div>
    </div>
  `;
}

// ─── Agents Tab ────────────────────────────────────────────────────────────────

function renderAgentsTab({
  draft,
  onAgentAdd,
  onAgentRemove,
  onAgentReorder,
  errors,
}: ProfileFormProps) {
  const agentsErr = errors.filter((e) => e.field === "agents");

  return html`
    <div class="ma-form-section">
      <div class="card-title">Agents</div>
      <div class="card-sub">
        ${draft.agentIds.length} agent${draft.agentIds.length !== 1 ? "s" : ""} in pipeline.
      </div>

      ${
        agentsErr.length > 0
          ? html`
            <div class="callout warn" style="margin-top: 8px;">
              ${agentsErr.map((e) => html`<div>${e.message}</div>`)}
            </div>
          `
          : nothing
      }

      ${
        draft.agentIds.length === 0
          ? html`
              <div class="ma-empty" style="margin-top: 24px">
                <div>No agents added yet.</div>
                <div class="muted">Add agents below to build your pipeline.</div>
              </div>
            `
          : html`
            <div class="ma-agent-list" style="margin-top: 16px;">
              ${draft.agentIds.map((agentId, index) => {
                const archIcon =
                  draft.architectureType === "sequential"
                    ? html`<span class="muted">${index === draft.agentIds.length - 1 ? "↓" : "→"}</span>`
                    : draft.architectureType === "supervisor"
                      ? html`<span class="muted">${index === 0 ? "⤵" : "⤴"}</span>`
                      : draft.architectureType === "parallel"
                        ? html`
                            <span class="muted">⤴</span>
                          `
                        : html`
                            <span class="muted">⬡</span>
                          `;

                return html`
                  <div class="ma-agent-row">
                    <span class="ma-agent-connector">${archIcon}</span>
                    <div class="ma-agent-chip">
                      <span class="mono">${agentId}</span>
                      ${
                        index === 0
                          ? html`
                              <span class="ma-badge" style="font-size: 10px">lead</span>
                            `
                          : nothing
                      }
                    </div>
                    <div class="row" style="gap: 4px; margin-left: auto;">
                      ${
                        index > 0
                          ? html`<button class="btn btn--icon" @click=${() => onAgentReorder(index, index - 1)} title="Move up">↑</button>`
                          : nothing
                      }
                      ${
                        index < draft.agentIds.length - 1
                          ? html`<button class="btn btn--icon" @click=${() => onAgentReorder(index, index + 1)} title="Move down">↓</button>`
                          : nothing
                      }
                      <button class="btn btn--icon btn--danger" @click=${() => onAgentRemove(agentId)} title="Remove">✕</button>
                    </div>
                  </div>
                `;
              })}
            </div>
          `
      }

      <div style="margin-top: 24px;">
        <div class="card-sub" style="margin-bottom: 8px;">Add Agent</div>
        <div class="row" style="gap: 8px;">
          <input
            type="text"
            class="input"
            placeholder="Agent ID (e.g. coder)"
            id="ma-agent-input"
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                const input = e.target as HTMLInputElement;
                if (input.value.trim()) {
                  onAgentAdd(input.value.trim());
                }
                input.value = "";
              }
            }}
          />
          <button
            class="btn"
            @click=${() => {
              const input = document.getElementById("ma-agent-input") as HTMLInputElement;
              if (input?.value.trim()) {
                onAgentAdd(input.value.trim());
                input.value = "";
              }
            }}
          >
            Add
          </button>
        </div>
        <div class="muted" style="font-size: 12px; margin-top: 6px;">
          Type an agent ID and press Enter, or use IDs from your agent list.
        </div>
      </div>
    </div>
  `;
}

// ─── Model Tab ─────────────────────────────────────────────────────────────────

function renderModelTab({ draft, onDraftUpdate }: ProfileFormProps) {
  const provider =
    STATIC_MODEL_PROVIDERS.find((p) => p.id === draft.modelProviderId) ?? STATIC_MODEL_PROVIDERS[0];

  return html`
    <div class="ma-form-section">
      <div class="card-title">Model Configuration</div>
      <div class="card-sub">Select the default model and provider for this profile.</div>

      <label class="field" style="margin-top: 16px;">
        <span>Provider</span>
        <select
          class="input"
          .value=${draft.modelProviderId}
          @change=${(e: Event) => {
            const p = (e.target as HTMLSelectElement).value;
            const prov = STATIC_MODEL_PROVIDERS.find((x) => x.id === p);
            onDraftUpdate({
              modelProviderId: p,
              defaultModelId: prov?.defaultModelId ?? "",
            });
          }}
        >
          ${STATIC_MODEL_PROVIDERS.map(
            (p) => html`
              <option value=${p.id} ?selected=${p.id === draft.modelProviderId}>
                ${p.name}
                ${p.status === "unconfigured" ? " (not configured)" : ""}
              </option>
            `,
          )}
        </select>
      </label>

      ${
        provider.status === "connected"
          ? html`
            <label class="field" style="margin-top: 16px;">
              <span>Model</span>
              <select
                class="input"
                .value=${draft.defaultModelId}
                @change=${(e: Event) => onDraftUpdate({ defaultModelId: (e.target as HTMLSelectElement).value })}
              >
                ${provider.availableModels.map(
                  (m) => html`
                    <option value=${m.id} ?selected=${m.id === draft.defaultModelId}>
                      ${m.label}
                      — ${m.contextWindow.toLocaleString()} context
                      ${m.supportsVision ? " · Vision" : ""}
                    </option>
                  `,
                )}
              </select>
            </label>

            ${
              provider.availableModels.find((m) => m.id === draft.defaultModelId)
                ? (() => {
                    const model = provider.availableModels.find(
                      (m) => m.id === draft.defaultModelId,
                    )!;
                    return html`
                    <div class="ma-model-info" style="margin-top: 12px;">
                      <div class="row" style="gap: 16px; flex-wrap: wrap;">
                        <span class="ma-badge">
                          ${model.supportsStreaming ? "✓ Streaming" : "No streaming"}
                        </span>
                        <span class="ma-badge">
                          ${model.supportsVision ? "✓ Vision" : "No vision"}
                        </span>
                        <span class="ma-badge">
                          $${model.costPer1kInput.toFixed(3)}/1K in · $${model.costPer1kOutput.toFixed(3)}/1K out
                        </span>
                        <span class="ma-badge">
                          ${model.contextWindow.toLocaleString()} context
                        </span>
                      </div>
                    </div>
                  `;
                  })()
                : nothing
            }
          `
          : html`
              <div class="callout warn" style="margin-top: 16px">
                This provider is not configured. Go to
                <strong>Model Providers</strong> to set up credentials.
              </div>
            `
      }
    </div>

    <div class="ma-form-section" style="margin-top: 24px;">
      <div class="card-title">Fallback Models</div>
      <div class="card-sub">
        If the primary model is unavailable, fall back models are tried in order.
      </div>
      <div class="callout info" style="margin-top: 12px;">
        Fallback model ordering is configured per-agent in the Agents tab.
      </div>
    </div>
  `;
}

// ─── Tools Tab ─────────────────────────────────────────────────────────────────

function renderToolsTab({ draft, onDraftUpdate }: ProfileFormProps) {
  const perms = draft.agentOverrides[draft.agentIds[0]]?.permissions ?? {};

  const permsList = [
    {
      id: "filesystemRead",
      label: "Read Files",
      description: "Read files from the filesystem",
      danger: "safe",
      group: "filesystem",
      value: perms.filesystemRead,
    },
    {
      id: "filesystemWrite",
      label: "Write Files",
      description: "Create or overwrite files",
      danger: "moderate",
      group: "filesystem",
      value: perms.filesystemWrite,
    },
    {
      id: "canExec",
      label: "Shell Execution",
      description: "Run shell commands",
      danger: "dangerous",
      group: "runtime",
      value: perms.canExec,
    },
    {
      id: "canSpawnSubagents",
      label: "Spawn Sub-Agents",
      description: "Spawn child agent sessions",
      danger: "moderate",
      group: "sessions",
      value: perms.canSpawnSubagents,
    },
    {
      id: "canNetwork",
      label: "Network Access",
      description: "Access external network resources",
      danger: "moderate",
      group: "web",
      value: perms.canNetwork,
    },
    {
      id: "canReadSessions",
      label: "Read Sessions",
      description: "Read session history and context",
      danger: "safe",
      group: "memory",
      value: perms.canReadSessions,
    },
    {
      id: "canWriteSharedWorkspace",
      label: "Shared Workspace",
      description: "Modify shared workspace files",
      danger: "moderate",
      group: "filesystem",
      value: perms.canWriteSharedWorkspace,
    },
  ];

  const groups = [
    {
      id: "filesystem",
      label: "File System",
      items: permsList.filter((p) => p.group === "filesystem"),
    },
    {
      id: "runtime",
      label: "Runtime & Execution",
      items: permsList.filter((p) => p.group === "runtime"),
    },
    {
      id: "sessions",
      label: "Sessions & Agents",
      items: permsList.filter((p) => p.group === "sessions"),
    },
    { id: "web", label: "Network & Web", items: permsList.filter((p) => p.group === "web") },
    {
      id: "memory",
      label: "Memory & Sessions",
      items: permsList.filter((p) => p.group === "memory"),
    },
  ].filter((g) => g.items.length > 0);

  function updatePerm(key: string, value: boolean | undefined) {
    const agentId = draft.agentIds[0];
    if (!agentId) {
      return;
    }
    onDraftUpdate({
      agentOverrides: {
        ...draft.agentOverrides,
        [agentId]: {
          ...draft.agentOverrides[agentId],
          permissions: {
            ...perms,
            [key]: value,
          },
        },
      },
    });
  }

  return html`
    <div class="ma-form-section">
      <div class="card-title">Tool Permissions</div>
      <div class="card-sub">
        Control agent capabilities. Each toggle maps to specific tool access.
        Permissions apply to the first agent in the pipeline.
      </div>

      <div class="ma-tool-groups" style="margin-top: 20px;">
        ${groups.map(
          (group) => html`
          <div class="ma-tool-group card">
            <div class="ma-tool-group-header">
              <span class="ma-tool-group-label">${group.label}</span>
              <div class="row" style="gap: 8px;">
                <button class="btn btn--sm" @click=${() => group.items.forEach((i) => updatePerm(i.id, true))}>Enable All</button>
                <button class="btn btn--sm" @click=${() => group.items.forEach((i) => updatePerm(i.id, false))}>Disable All</button>
              </div>
            </div>
            <div class="ma-tool-list">
              ${group.items.map(
                (tool) => html`
                <div class="ma-tool-row">
                  <span class="ma-tool-name">${tool.label}</span>
                  <span class="ma-tool-desc muted">${tool.description}</span>
                  <span class="ma-tool-danger ma-tool-danger--${tool.danger}">
                    ${tool.danger === "dangerous" ? "⚠ dangerous" : tool.danger === "moderate" ? "⚡ moderate" : "✓ safe"}
                  </span>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      .checked=${tool.value === true}
                      @change=${(e: Event) => updatePerm(tool.id, (e.target as HTMLInputElement).checked ? true : undefined)}
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              `,
              )}
            </div>
          </div>
        `,
        )}
      </div>
    </div>
  `;
}

// ─── Limits Tab ───────────────────────────────────────────────────────────────

function renderLimitsTab({ draft, onDraftUpdate, errors }: ProfileFormProps) {
  const limits = draft.limits;
  const maxCostErr = errors.find((e) => e.field === "limits.maxCost");
  const maxDurErr = errors.find((e) => e.field === "limits.maxDurationSeconds");
  const maxSubErr = errors.find((e) => e.field === "limits.maxConcurrentSubagents");

  return html`
    <div class="ma-form-section">
      <div class="card-title">Execution Limits</div>
      <div class="card-sub">Global limits applied to this profile's execution.</div>

      <div class="ma-limits-grid" style="margin-top: 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
        <label class="field">
          <span>Max Iterations</span>
          <input
            type="number"
            class="input ${errors.find((e) => e.field === "limits.maxIterations") ? "input--error" : ""}"
            min="1"
            max="10000"
            .value=${String(limits.maxIterations)}
            @input=${(e: Event) =>
              onDraftUpdate({
                limits: { ...limits, maxIterations: Number((e.target as HTMLInputElement).value) },
              })}
          />
          <span class="field-hint">Maximum agent loop iterations</span>
        </label>

        <label class="field">
          <span>Max Tokens</span>
          <input
            type="number"
            class="input"
            min="1000"
            .value=${String(limits.maxTokens)}
            @input=${(e: Event) =>
              onDraftUpdate({
                limits: { ...limits, maxTokens: Number((e.target as HTMLInputElement).value) },
              })}
          />
          <span class="field-hint">Per-agent token budget</span>
        </label>

        <label class="field">
          <span>Max Cost ($)</span>
          <input
            type="number"
            class="input ${maxCostErr ? "input--error" : ""}"
            min="0.01"
            step="0.01"
            .value=${String(limits.maxCost)}
            @input=${(e: Event) =>
              onDraftUpdate({
                limits: { ...limits, maxCost: Number((e.target as HTMLInputElement).value) },
              })}
          />
          ${
            maxCostErr
              ? html`<span class="field-error">${maxCostErr.message}</span>`
              : html`
                  <span class="field-hint">Total budget for execution</span>
                `
          }
        </label>

        <label class="field">
          <span>Max Duration (seconds)</span>
          <input
            type="number"
            class="input ${maxDurErr ? "input--error" : ""}"
            min="60"
            .value=${String(limits.maxDurationSeconds)}
            @input=${(e: Event) =>
              onDraftUpdate({
                limits: {
                  ...limits,
                  maxDurationSeconds: Number((e.target as HTMLInputElement).value),
                },
              })}
          />
          ${
            maxDurErr
              ? html`<span class="field-error">${maxDurErr.message}</span>`
              : html`
                  <span class="field-hint">Execution timeout</span>
                `
          }
        </label>
      </div>

      <label class="field" style="margin-top: 16px;">
        <span>Max Concurrent Subagents</span>
        <input
          type="range"
          min="1"
          max="16"
          .value=${String(limits.maxConcurrentSubagents)}
          @input=${(e: Event) =>
            onDraftUpdate({
              limits: {
                ...limits,
                maxConcurrentSubagents: Number((e.target as HTMLInputElement).value),
              },
            })}
          style="width: 200px;"
        />
        <span class="mono">${limits.maxConcurrentSubagents}</span>
        ${maxSubErr ? html`<span class="field-error">${maxSubErr.message}</span>` : nothing}
      </label>
    </div>

    <div class="ma-form-section" style="margin-top: 24px;">
      <div class="card-title">Reset to Defaults</div>
      <button
        class="btn"
        style="margin-top: 8px;"
        @click=${() => onDraftUpdate({ limits: { ...DEFAULT_EXECUTION_LIMITS } })}
      >
        Reset All Limits
      </button>
    </div>
  `;
}

// ─── Builder Tab ───────────────────────────────────────────────────────────────

function renderBuilderTab({
  draft,
  onDraftUpdate,
  onAgentAdd,
  onAgentRemove,
  onAgentReorder,
  selectedNodeId,
  onNodeSelect,
}: ProfileFormProps) {
  const archLabel =
    draft.architectureType.charAt(0).toUpperCase() + draft.architectureType.slice(1);

  // Run real-time validation
  const validation = validateExecutionGraph(draft);

  // Build the appropriate workflow builder based on architecture type
  const builderProps = {
    draft,
    onDraftUpdate,
    onAgentAdd,
    onAgentRemove,
    onAgentReorder,
    selectedNodeId: selectedNodeId ?? null,
    onNodeSelect: onNodeSelect ?? (() => {}),
  };

  return html`
    <div class="ma-form-section">
      <div class="card-title">${archLabel} Workflow Builder</div>
      <div class="card-sub">
        Visual editor for ${draft.architectureType} architecture.
        ${
          draft.architectureType === "custom"
            ? "Build free-form graphs with arbitrary connections."
            : "Drag agents to reorder, click nodes to configure."
        }
      </div>

      ${renderValidationResult(validation)}

      <div class="ma-builder-wrapper" style="margin-top: 16px;">
        ${
          draft.architectureType === "sequential"
            ? renderSequentialWorkflowBuilder(builderProps as SequentialBuilderProps)
            : draft.architectureType === "supervisor"
              ? renderSupervisorWorkflowBuilder(builderProps as SupervisorBuilderProps)
              : draft.architectureType === "parallel"
                ? renderParallelWorkflowBuilder(builderProps as ParallelBuilderProps)
                : renderCustomGraphBuilder({
                    draft,
                    onDraftUpdate,
                    onAgentAdd,
                    onAgentRemove,
                  } as CustomGraphBuilderProps)
        }
      </div>

      ${
        draft.architectureType === "custom"
          ? html`
              <div class="callout info" style="margin-top: 16px">
                <strong>Note:</strong> Full drag-and-drop graph editing with custom edges, cycle detection, and
                conditional routing is implemented in JAISIU-854. This preview shows the agent list. Connect
                agents in the graph editor.
              </div>
            `
          : nothing
      }
    </div>
  `;
}

// ─── Preview Tab ───────────────────────────────────────────────────────────────

function renderPreviewTab({ draft }: ProfileFormProps) {
  return html`
    <div class="ma-form-section">
      <div class="card-title">Profile Preview</div>
      <div class="card-sub">Review the complete configuration before saving.</div>

      <div class="ma-preview" style="margin-top: 20px;">
        <div class="card" style="margin-bottom: 16px;">
          <div class="ma-preview-row">
            <span class="muted">Name</span>
            <span>${draft.name || "(unnamed)"}</span>
          </div>
          <div class="ma-preview-row">
            <span class="muted">Architecture</span>
            <span class="ma-badge">${draft.architectureType}</span>
          </div>
          <div class="ma-preview-row">
            <span class="muted">Agents</span>
            <span>${draft.agentIds.length}</span>
          </div>
          <div class="ma-preview-row">
            <span class="muted">Default Model</span>
            <span class="mono">${draft.defaultModelId || "(none)"}</span>
          </div>
          <div class="ma-preview-row">
            <span class="muted">Max Cost</span>
            <span>$${(draft.limits.maxCost ?? 0).toFixed(2)}</span>
          </div>
          <div class="ma-preview-row">
            <span class="muted">Max Duration</span>
            <span>${Math.round((draft.limits.maxDurationSeconds ?? 0) / 60)} min</span>
          </div>
        </div>

        ${
          draft.agentIds.length > 0
            ? html`
              <div class="card">
                <div class="card-title" style="margin-bottom: 12px;">Agent Pipeline</div>
                <div class="ma-preview-pipeline">
                  ${draft.agentIds.map(
                    (id, i) => html`
                    <div class="ma-preview-agent">
                      <div class="ma-preview-agent-icon">
                        ${draft.architectureType === "sequential" ? "→" : "⤵"}
                      </div>
                      <div class="mono">${id}</div>
                      <div class="muted" style="font-size: 11px;">${draft.agentOverrides[id]?.role ?? (i === 0 ? "lead" : "worker")}</div>
                    </div>
                  `,
                  )}
                </div>
              </div>
            `
            : nothing
        }
      </div>
    </div>
  `;
}

// ─── Nesting Tab (JAISIU-878) ─────────────────────────────────────────────────

function renderNestingTab({
  draft,
  nestingTree,
  nestingValidation,
  expandedGraph,
  onNestingToggle,
  onExpandProfile,
  onValidateNesting,
}: ProfileFormProps) {
  return html`
    <div class="ma-form-section">
      <div class="card-title">Nested Profiles</div>
      <div class="card-sub">
        Configure nested profile references to build hierarchical workflows.
        Nested profiles can reference other profiles as sub-workflows.
      </div>

      <div class="ma-nesting-info" style="margin-top: 16px;">
        <div class="callout info">
          <strong>How Nested Profiles Work:</strong>
          <ul style="margin: 8px 0 0 16px; padding: 0;">
            <li>Profiles can reference other profiles as sub-workflows</li>
            <li>Nested execution shares context variables with the parent</li>
            <li>Circular references are detected and rejected</li>
            <li>Maximum nesting depth defaults to 4 levels</li>
          </ul>
        </div>
      </div>

      <div class="ma-nesting-actions" style="margin-top: 16px; display: flex; gap: 8px;">
        <button
          class="btn"
          @click=${() => onValidateNesting?.(draft.id ?? "")}
          ?disabled=${!draft.id}
        >
          Validate Nesting
        </button>
        <button
          class="btn"
          @click=${() => onExpandProfile?.(draft.id ?? "")}
          ?disabled=${!draft.id}
        >
          Expand Graph
        </button>
      </div>

      ${
        nestingValidation
          ? html`
            <div style="margin-top: 16px;">
              ${renderNestingValidation(
                nestingValidation.cycles ?? [],
                nestingValidation.maxDepth ?? 0,
                nestingValidation.maxAllowedDepth ?? 4,
              )}
            </div>
          `
          : nothing
      }

      ${
        expandedGraph
          ? html`
            <div style="margin-top: 16px;">
              ${renderExpandedGraph(expandedGraph as Parameters<typeof renderExpandedGraph>[0])}
            </div>
          `
          : nothing
      }

      ${
        nestingTree
          ? html`
            <div class="ma-nesting-tree" style="margin-top: 16px;">
              <div class="card-title">Profile Tree</div>
              <div style="margin-top: 12px;">
                ${renderNestedProfile(
                  nestingTree as unknown as NestedProfileDisplay,
                  0,
                  true,
                  onNestingToggle,
                )}
              </div>
            </div>
          `
          : nothing
      }
    </div>

    <div class="ma-form-section" style="margin-top: 24px;">
      <div class="card-title">Add Nested Profile Reference</div>
      <div class="card-sub">
        Add a reference to another profile as a sub-workflow.
      </div>

      <div style="margin-top: 16px;">
        <label class="field">
          <span>Profile ID</span>
          <input
            type="text"
            class="input"
            placeholder="e.g. profile_12345"
            id="ma-nested-profile-input"
          />
          <span class="field-hint">Enter the ID of the profile to nest</span>
        </label>

        <label class="field" style="margin-top: 12px;">
          <span>Max Nesting Depth Override</span>
          <input
            type="number"
            class="input"
            min="1"
            max="10"
            value="4"
            id="ma-nested-max-depth"
          />
          <span class="field-hint">Override the max depth for this sub-profile</span>
        </label>

        <label class="field" style="margin-top: 12px;">
          <span class="toggle-label">
            <input type="checkbox" id="ma-nested-inherit-context" checked />
            <span>Inherit parent context</span>
          </span>
          <span class="field-hint">Share variables from parent execution</span>
        </label>

        <button
          class="btn"
          style="margin-top: 16px;"
          @click=${() => {
            const input = document.getElementById("ma-nested-profile-input") as HTMLInputElement;
            if (input?.value.trim()) {
              // Note: In a full implementation, this would add a NestedProfileRef
              // For now, we show a placeholder message
              console.log("Add nested profile:", input.value.trim());
              input.value = "";
            }
          }}
        >
          Add Nested Profile
        </button>
      </div>
    </div>
  `;
}
